'use client'

import React, { useEffect, useMemo, useState } from 'react'
import { Header } from '@/payload-types'
import {
  NavigationMenu,
  NavigationMenuContent,
  NavigationMenuItem,
  NavigationMenuLink,
  NavigationMenuList,
  NavigationMenuTrigger,
} from '@/components/ui/navigation-menu'
import { Link } from '@/i18n/routing'
import { cn } from '@/utilities/cn'
import { getCategoryHref } from '@/utilities/categoryHref'
import { useTranslations } from 'next-intl'
import { usePathname } from '@/i18n/routing'
import { useSearchParams } from 'next/navigation'
import { LayoutGroup, motion } from 'framer-motion'

interface HeaderNavProps {
  header: Header
}

// Prevent hover from opening dropdown — click only. Keep this: a three-level
// mega-menu that opens on hover is miserable on a trackpad and unusable on touch.
const preventHover = {
  onPointerEnter: (e: React.PointerEvent) => e.preventDefault(),
  onPointerMove: (e: React.PointerEvent) => e.preventDefault(),
  onPointerLeave: (e: React.PointerEvent) => e.preventDefault(),
}

/**
 * The active "slime" thumb — a transparent, backdrop-blurred liquid-glass pill
 * (see index.css `.navThumb`) that slides between items. `layoutId` is global in
 * framer-motion, so this lives inside <LayoutGroup id="header-desktop"> to stay
 * isolated from the mobile drawer's thumb (PHASE-08 §3.3). Spring matches the
 * prototype's LiquidGlassNavigation (stiffness ~300, damping 30).
 */
function NavThumb() {
  return (
    <motion.span
      layoutId="nav-thumb-desktop"
      aria-hidden
      transition={{ type: 'spring', stiffness: 300, damping: 30 }}
      className="navThumb"
    />
  )
}

// Panel width + column count follow the branch shape (§3.1) rather than a fixed
// 800px. One column does not need 800px; a wall of seed categories needs more.
const PANEL_BY_COLS: Record<number, string> = {
  1: 'w-[320px] grid-cols-1',
  2: 'w-[560px] grid-cols-2',
  3: 'w-[760px] grid-cols-3',
  4: 'w-[900px] grid-cols-4',
}

// Shared per-item classes: `group` for the hover pill, `navJiggle` for the
// springy hover/press scale. Text is neutral (foreground/muted) — the glass thumb
// is the indicator, no coloured accent on the labels.
const ITEM_BASE =
  'group navJiggle relative h-11 px-3 flex items-center text-base font-medium transition-colors'

export function HeaderNav({ header }: HeaderNavProps) {
  const navItems = header.navItems || []
  const t = useTranslations('common')
  const pathname = usePathname()
  const searchParams = useSearchParams()

  // Check if a nav item URL matches the current route
  const isActive = (url: string) => {
    if (url === '#') return false
    // For /shop?category=X URLs
    if (url.startsWith('/shop?category=')) {
      const catId = url.split('category=')[1]
      return pathname === '/shop' && searchParams.get('category') === catId
    }
    // For direct URLs
    return pathname === url
  }

  // A top-level item is active when it, or any descendant (sub / sub-sub), is the
  // current route. This is what lights the parent when you're three levels deep.
  const isItemActiveByRoute = (item: NonNullable<Header['navItems']>[number]) => {
    const itemUrl = getCategoryHref(item.categoryId, item.link.url)
    return (
      isActive(itemUrl) ||
      !!item.subNavItems?.some((sub) => {
        const subUrl = getCategoryHref(sub.categoryId, sub.link.url)
        if (isActive(subUrl)) return true
        return sub.subSubNavItems?.some((subsub) =>
          isActive(getCategoryHref(subsub.categoryId, subsub.link.url)),
        )
      })
    )
  }

  const routeActiveId = useMemo(
    () => navItems.find((it) => isItemActiveByRoute(it))?.id,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [navItems, pathname, searchParams],
  )

  // The thumb follows a click immediately (the LiquidGlassNavigation feel), then
  // re-syncs to the route whenever navigation changes which item is active.
  const [activeId, setActiveId] = useState<string | null | undefined>(routeActiveId)
  useEffect(() => {
    setActiveId(routeActiveId)
  }, [routeActiveId])

  return (
    <LayoutGroup id="header-desktop">
      <NavigationMenu className="h-full">
        <NavigationMenuList className="h-full gap-1 lg:gap-2">
          {navItems.map((item) => {
            const hasSubNav = item.subNavItems && item.subNavItems.length > 0
            const itemUrl = getCategoryHref(item.categoryId, item.link.url)
            const isThumb = activeId === item.id

            if (hasSubNav) {
              const subs = item.subNavItems ?? []
              const anyGrandchildren = subs.some(
                (s) => s.subSubNavItems && s.subSubNavItems.length > 0,
              )
              const cols = anyGrandchildren
                ? Math.min(subs.length, 4)
                : subs.length > 8
                  ? 2
                  : 1
              const panelClass = PANEL_BY_COLS[cols] ?? PANEL_BY_COLS[3]

              return (
                <NavigationMenuItem key={item.id} className="h-full flex items-center">
                  <NavigationMenuTrigger
                    aria-current={isThumb ? 'page' : undefined}
                    onClick={() => setActiveId(item.id)}
                    className={cn(
                      ITEM_BASE,
                      '!bg-transparent hover:!bg-transparent focus:!bg-transparent data-[state=open]:!bg-transparent',
                      isThumb
                        ? 'text-foreground'
                        : 'text-muted-foreground hover:text-foreground data-[state=open]:text-foreground',
                    )}
                    {...preventHover}
                  >
                    {isThumb ? (
                      <NavThumb />
                    ) : (
                      <span aria-hidden className="navHoverPill" />
                    )}
                    <span className="relative z-10">{item.link.label}</span>
                  </NavigationMenuTrigger>
                  <NavigationMenuContent
                    onPointerEnter={(e) => e.preventDefault()}
                    onPointerLeave={(e) => e.preventDefault()}
                  >
                    <ul
                      className={cn(
                        'grid gap-3 p-4 max-h-[calc(100vh-6rem)] overflow-y-auto max-w-[calc(100vw-2rem)]',
                        panelClass,
                      )}
                    >
                        {/* "View All" link for the top-level category */}
                        <li className="col-span-full border-b border-border pb-3 mb-1">
                          <NavigationMenuLink asChild>
                            <Link
                              href={itemUrl !== '#' ? itemUrl : '/shop'}
                              className="text-sm font-semibold text-jade hover:underline outline-none transition-colors"
                            >
                              {t('viewAll')} — {item.link.label}
                            </Link>
                          </NavigationMenuLink>
                        </li>
                        {(!item.subNavItems || item.subNavItems.length === 0) && (
                          <li className="col-span-full text-sm text-muted-foreground py-2">
                            {t('noSubcategories')}
                          </li>
                        )}
                        {item.subNavItems?.map((subItem) => {
                          const subItemUrl = getCategoryHref(subItem.categoryId, subItem.link.url)

                          const hasSubSubNav =
                            subItem.subSubNavItems && subItem.subSubNavItems.length > 0

                          return (
                            <li key={subItem.id} className="flex flex-col gap-2 p-2">
                              <NavigationMenuLink asChild>
                                <Link
                                  href={subItemUrl}
                                  className="text-base font-medium leading-none text-foreground hover:text-jade hover:underline outline-none transition-colors"
                                >
                                  {subItem.link.label}
                                </Link>
                              </NavigationMenuLink>

                              {hasSubSubNav && (
                                <ul className="flex flex-col gap-2">
                                  {/* "View All" for this sub-category */}
                                  <li>
                                    <NavigationMenuLink asChild>
                                      <Link
                                        href={subItemUrl !== '#' ? subItemUrl : '/shop'}
                                        className="text-xs font-semibold text-jade hover:underline outline-none transition-colors"
                                      >
                                        {t('viewAll')}
                                      </Link>
                                    </NavigationMenuLink>
                                  </li>
                                  {subItem.subSubNavItems?.map((subSubItem) => {
                                    const subSubItemUrl = getCategoryHref(
                                      subSubItem.categoryId,
                                      subSubItem.link.url,
                                    )

                                    return (
                                      <li key={subSubItem.id}>
                                        <NavigationMenuLink asChild>
                                          <Link
                                            href={subSubItemUrl}
                                            className="text-sm text-muted-foreground hover:text-jade hover:underline line-clamp-1 outline-none transition-colors"
                                          >
                                            {subSubItem.link.label}
                                          </Link>
                                        </NavigationMenuLink>
                                      </li>
                                    )
                                  })}
                                </ul>
                              )}
                            </li>
                          )
                        })}
                    </ul>
                  </NavigationMenuContent>
                </NavigationMenuItem>
              )
            }

            return (
              <NavigationMenuItem key={item.id} className="h-full flex items-center">
                <NavigationMenuLink asChild>
                  <Link
                    href={itemUrl}
                    aria-current={isThumb ? 'page' : undefined}
                    onClick={() => setActiveId(item.id)}
                    className={cn(
                      ITEM_BASE,
                      isThumb ? 'text-foreground' : 'text-muted-foreground hover:text-foreground',
                    )}
                  >
                    {isThumb ? (
                      <NavThumb />
                    ) : (
                      <span aria-hidden className="navHoverPill" />
                    )}
                    <span className="relative z-10">{item.link.label}</span>
                  </Link>
                </NavigationMenuLink>
              </NavigationMenuItem>
            )
          })}
        </NavigationMenuList>
      </NavigationMenu>
    </LayoutGroup>
  )
}
